/**
 * normalizeRole
 *
 * Normalizes a role/profile name string for case-insensitive,
 * diacritics-insensitive comparisons across the platform.
 *
 * Used by both the permission predicates (`shared/utils/permissions`)
 * and the profile label mapper (`features/users/utils/profileLabels`).
 *
 * @module shared/utils/normalizeRole
 */

/**
 * Normalizes a role string for case-insensitive, diacritics-insensitive comparison.
 *
 * Steps:
 * 1. NFD decompose → strip combining diacritical marks (e.g. `á` → `a`)
 * 2. Uppercase
 * 3. Replace any run of non-alphanumeric characters with a single `_`
 * 4. Strip leading/trailing underscores
 *
 * @param value - Raw role string from the API (e.g. `"Gestor"`, `"Admin Básico"`)
 * @returns Normalized uppercase string (e.g. `"ADMIN_BASICO"`)
 *
 * @example
 * ```ts
 * normalizeRole('Admin Básico')  // → 'ADMIN_BASICO'
 * normalizeRole('gestor')        // → 'GESTOR'
 * normalizeRole('SUPER_ADMIN')   // → 'SUPER_ADMIN'
 * ```
 */
export const normalizeRole = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
